import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { formatDateTime } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { Person } from '../ui/Avatar';
import { useMembers } from '../ui/UserSelect';
import { listAuditEvents, type AuditEvent } from './api';

/**
 * The audit trail (context.md sections 25-26).
 *
 * `actor_type` is the column worth having in front: an action taken by an automation, an AI agent or
 * a Safe Cortex operator during a support session reads very differently from one a colleague took,
 * and a trail that showed them all the same way would hide exactly what it exists to reveal.
 */

const ACTOR_TONE: Record<string, BadgeTone> = {
  USER: 'neutral',
  PLATFORM_ADMIN: 'warning',
  AI_AGENT: 'info',
  AUTOMATION: 'info',
  INTEGRATION: 'info',
  SYSTEM: 'neutral',
};

/**
 * What happened, in words, with the code underneath.
 *
 * The code is what somebody filters by and what support quotes, so it stays; the sentence is what
 * makes the page readable. An action this build has no phrase for shows the code alone rather than
 * a wrong guess.
 */
function actionLabel(action: string): string {
  const label = t(`audit.action.${action}`);

  return label === `audit.action.${action}` ? t('audit.unknownAction') : label;
}

export function AuditPage(): React.JSX.Element {
  const [action, setAction] = useState('');
  const [applied, setApplied] = useState('');

  const events = useQuery({
    queryKey: ['audit', applied],
    queryFn: () => listAuditEvents(applied ? { action: applied } : {}),
  });

  const { options: people } = useMembers();

  const personFor = (actorId: string | null): { name: string; url?: string | null } | null => {
    const found = actorId === null ? undefined : people.find((person) => person.id === actorId);

    return found ? { name: found.name, url: found.avatarUrl } : null;
  };

  const columns: Column<AuditEvent>[] = [
    {
      key: 'action',
      header: t('audit.action'),
      render: (event) => (
        <span className="cell-stack">
          <span className="cell-stack">
            <span>{actionLabel(event.action)}</span>
            <code className="muted small">{event.action}</code>
          </span>
          {event.entity_type && (
            <span className="muted small">
              {event.entity_type} {event.entity_id ? `· ${event.entity_id.slice(0, 8)}` : ''}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'actor',
      header: t('audit.actor'),
      render: (event) => (
        <span className="cell-stack">
          {/*
           * The type stays in front: an action taken by an automation or an agent is a different
           * kind of fact from one a person took, and that is the first thing to read.
           *
           * The name is added underneath when the actor is somebody still on the team. An audit row
           * about a person who has since been removed keeps only the type — inventing a name for an
           * id nothing resolves would be worse than saying nothing.
           */}
          <Badge tone={ACTOR_TONE[event.actor_type] ?? 'neutral'}>
            {t(`audit.actor.${event.actor_type}`)}
          </Badge>
          {personFor(event.actor_id) && <Person size="xs" {...personFor(event.actor_id)!} />}
          {event.operator_id && <span className="muted small">{t('audit.viaSupport')}</span>}
        </span>
      ),
    },
    {
      key: 'ip',
      header: t('audit.ip'),
      secondary: true,
      render: (event) => event.ip ?? '—',
    },
    {
      key: 'when',
      header: t('audit.when'),
      align: 'end',
      render: (event) => formatDateTime(event.occurred_at),
    },
  ];

  return (
    <div className="view">
      <PageHeader title={t('nav.audit')} description={t('audit.description')} />

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(action.trim());
        }}
      >
        <label className="field-search">
          <input
            type="search"
            value={action}
            placeholder={t('audit.filterPlaceholder')}
            onChange={(event) => setAction(event.target.value)}
          />
        </label>

        <button type="submit" className="button button--quiet button--compact">
          {t('crm.search')}
        </button>

        {applied && (
          <button
            type="button"
            className="chip"
            onClick={() => {
              setAction('');
              setApplied('');
            }}
          >
            {t('crm.clearSearch')}
          </button>
        )}
      </form>

      <Panel>
        <DataTable
          tableKey="audit"
          rows={events.data?.events}
          columns={columns}
          rowKey={(event) => event.id}
          isLoading={events.isLoading}
          error={events.error}
          onRetry={() => void events.refetch()}
          empty={{ title: t('audit.empty'), description: t('audit.emptyBody') }}
        />
      </Panel>
    </div>
  );
}
